import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import ContentRelated from 'components/ContentRelated';
import { useCallContentRelated } from 'components/Dashboard/utilities/callContentRelated';
import useCallRelatedStakeholders from 'components/Dashboard/KeyUpdates/useCallStakeholders';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import { store } from 'components/Store';
import InteractionFormFields from './InteractionFormFields';

const CrmInteractionDetail = () => {
  const { interactionId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    date: '',
    type: '',
    description: '',
    initiator: '',
    stakeholders: [],
    members: [],
  });

  const initialFormRef = useRef(null);

  const { state } = useContext(store);
  const { generalApiCall } = useGeneralApiCall();
  const [heightContainer, containerRef] = useHeightContainer();
  const { contentResults, setContentResults } = useContentResults();
  const [isLoadingRelatedContent, setIsLoadingRelatedContent] = useState(true);
  const { callContentRelated } = useCallContentRelated();
  const { callRelatedStakeholders } = useCallRelatedStakeholders();
  const numberOfStakeHoldersToShow = parseInt(import.meta.env.VITE_APP_RELATED_STAKEHOLDERS_TO_SHOW);
  const crmSection = contentResults['CrmInteractions'];

  useEffect(() => {
    const source = new AbortController();
    loadInteraction(source);
    return () => source.abort('Interaction detail cancelled');
  }, [interactionId]);

  const loadInteraction = async (source) => {
    setIsLoading(true);
    setIsLoadingRelatedContent(true);
    const result = await generalApiCall({
      method: 'get',
      pathname: `/api/crm-interaction/${interactionId}`,
      needsAuthentication: true,
      requestSource: source,
      notShowErrorMessage: true,
    });
    if (result) {
      const loadedForm = {
        title: result.title ?? '',
        date: result.dateTime ? dayjs(result.dateTime).format('YYYY-MM-DD') : '',
        type: result.interactionType ?? '',
        description: result.htmlBody ?? result.description ?? '',
        initiator:
          result.initiatedByOrganisation === true
            ? 'Organisation'
            : result.initiatedByOrganisation === false
              ? 'Stakeholder'
              : '',
        stakeholders: (result.stakeholderContacts ?? []).map((c) => ({
          value: c.id,
          label: c.fullName ?? '',
          id: c.id,
          email: '',
          imageId: c.imageId ?? null,
          imageUrl: null,
        })),
        members: (result.associatedUserIds ?? []).map((id, idx) => {
          const nameFromResponse = result.associatedUserFullNames?.[idx];
          const member = !nameFromResponse ? state.team?.allMembers?.find((m) => m.id === id) : null;
          return { value: id, label: nameFromResponse ?? member?.fullName ?? member?.emailAddress ?? `User ${id}` };
        }),
      };
      setForm(loadedForm);
      initialFormRef.current = loadedForm;
      setIsLoading(false);
      await loadRelatedContent(result, source);
    } else {
      setIsLoading(false);
      setIsLoadingRelatedContent(false);
    }
  };

  const loadRelatedContent = async (interaction, source) => {
    const stakeholderIds = interaction.contactIds ?? [];

    const contentRelated = await callContentRelated({
      objectId: `CrmInteraction-${interactionId}`,
      objectType: 'CrmInteraction',
      source,
    });

    let relatedContentWithStakeholders = contentRelated ?? { hits: {} };
    let stakeholdersRendered = [];
    let contactsIdsCalled = [];

    if (stakeholderIds.length > 0) {
      const initialIds = stakeholderIds.slice(0, numberOfStakeHoldersToShow);
      contactsIdsCalled = initialIds;
      const filters = initialIds.map((id) => ({ field: 'id', value: `${id}`, operator: 'str_eq' }));
      const stakeHolderResults = await callRelatedStakeholders({ stakeholderFilters: filters, source });
      stakeholdersRendered = stakeHolderResults?.data?.hits ?? [];
      relatedContentWithStakeholders = {
        ...relatedContentWithStakeholders,
        hits: { ...relatedContentWithStakeholders.hits, StakeHolders: stakeholdersRendered },
      };
    }

    setContentResults((prev) => ({
      ...prev,
      CrmInteractions: {
        ...prev.CrmInteractions,
        contentRelated: relatedContentWithStakeholders,
        relatedStakeholders:
          stakeholderIds.length > 0 ? stakeholderIds.filter((id) => !contactsIdsCalled.includes(id)) : null,
        relatedStakeholdersRendered: stakeholdersRendered.length,
      },
    }));
    setIsLoadingRelatedContent(false);
  };

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.title.trim()) {
      createNotification('danger', 'Please enter a title');
      return;
    }
    if (!form.type) {
      createNotification('danger', 'Please select an interaction type');
      return;
    }
    if (!form.date) {
      createNotification('danger', 'Please select a date');
      return;
    }
    setIsSaving(true);
    const result = await generalApiCall({
      method: 'post',
      pathname: '/api/crm-interaction/update',
      requestProperties: {
        id: Number(interactionId),
        title: form.title,
        date: form.date,
        type: form.type,
        description: form.description,
        initiatedByOrganisation:
          form.initiator === 'Organisation' ? true : form.initiator === 'Stakeholder' ? false : null,
        contactIds: form.stakeholders.map((p) => p.id),
        userIds: form.members.map((m) => m.value),
      },
      needsAuthentication: true,
    });
    setIsSaving(false);
    if (result) {
      initialFormRef.current = form;
      createNotification('success', 'Interaction saved');
    }
  };

  const isDirty = JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  const contentRelatedData = {
    objectId: `CrmInteraction-${interactionId}`,
    objectType: 'CrmInteraction',
    id: Number(interactionId),
    title: form.title,
    contentUrl: `/people/interactions/${interactionId}`,
  };

  if (isLoading) {
    return (
      <div className='main-content pl-md-4 pl-3 pr-md-4 pr-3 pt-4'>
        <Skeleton height={32} width={200} className='mb-4' />
        <Skeleton height={20} width={300} className='mb-2' />
        <Skeleton height={20} width={200} className='mb-2' />
      </div>
    );
  }

  return (
    <div className='main-content px-md-4 px-3'>
      <div className='row px-0 piece-content-container' ref={containerRef}>
        <div className='general-content-column padding-to-nav p-0 z-100'>
          <div className='content-container h-100'>
            <CustomScrollbar
              className='simple-scrollbar indented-scrollbar-small'
              style={{ height: `${heightContainer}px` }}
              maximalThumbYSize={100}
            >
              <div className='p-3 p-lg-4 pb-5'>
                <InteractionFormFields form={form} onChange={handleChange} />
                <div className='d-flex justify-content-end'>
                  <button
                    className='general-button action-button rounded-pill py-2 px-4 paragraph-p2'
                    onClick={handleSave}
                    disabled={isSaving || !isDirty}
                  >
                    {isSaving ? 'Updating...' : 'Update interaction'}
                  </button>
                </div>
              </div>
            </CustomScrollbar>
          </div>
        </div>

        <div className='related-content-column show-lg px-0'>
          <ContentRelated
            data={contentRelatedData}
            content={crmSection?.contentRelated}
            relatedStakeholders={crmSection?.relatedStakeholders}
            relatedStakeholdersRendered={crmSection?.relatedStakeholdersRendered ?? 0}
            mainSection='CrmInteractions'
            isLoadingRelatedContent={isLoadingRelatedContent}
          />
        </div>
      </div>
    </div>
  );
};

export default CrmInteractionDetail;
